<template>
  <div class="usage-dashboard">
    <section class="dashboard-metrics" aria-label="기기 사용 통계">
      <article
        v-for="metric in metrics"
        :key="metric.label"
        class="dashboard-metric"
        :class="metric.tone"
      >
        <div class="metric-main">
          <span class="metric-symbol" aria-hidden="true">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
            >
              <template v-if="metric.tone === 'metric-total'">
                <rect x="3" y="4" width="18" height="13" />
                <path d="M12 17v4M7 21h10" />
              </template>
              <template v-else-if="metric.tone === 'metric-active'">
                <path d="M12 2v10M6 5a9 9 0 1 0 12 0" />
              </template>
              <template v-else-if="metric.tone === 'metric-inactive'">
                <path d="M12 2v10M6 5a9 9 0 1 0 12 0" />
              </template>
              <template v-else>
                <circle cx="12" cy="12" r="9" />
                <path d="M12 6v6l4 2" />
              </template>
            </svg>
          </span>
          <div class="metric-copy">
            <strong v-fit-metric="metric.value" class="metric-value">
              <span class="metric-value-line"
                ><span
                  v-for="(part, index) in metric.value.match(
                    /[\d,.]+|[^\d,.]+/g
                  )"
                  :key="index"
                  :class="{ 'metric-unit': !/^[\d,.]+$/.test(part) }"
                  >{{ part }}</span
                ></span
              >
            </strong>
            <h3>{{ metric.label }}</h3>
          </div>
        </div>
      </article>
    </section>

    <section
      class="dashboard-card distribution-card"
      aria-labelledby="distribution-title"
    >
      <header class="dashboard-heading">
        <h3 id="distribution-title">사용시간분포</h3>
        <button
          type="button"
          class="dashboard-button"
          @click="$emit('statistics')"
        >
          사용시간 통계 보기
        </button>
      </header>
      <div v-if="devices.length" class="dashboard-chart">
        <Bar
          :data="chartData"
          :options="chartOptions"
          role="img"
          :aria-label="distributionLabel"
        />
      </div>
      <div v-else class="dashboard-empty chart-empty">
        조회된 기기가 없습니다.
      </div>
      <p v-if="distribution.unknown" class="dashboard-note">
        누적 사용시간 미확인 {{ distribution.unknown }}대는 구간 집계에서
        제외됩니다.
      </p>
      <details class="chart-values">
        <summary>구간별 기기 수</summary>
        <ul>
          <li v-for="(bucket, index) in buckets" :key="bucket.label">
            {{ bucket.label }}: {{ distribution.counts[index] }}대
          </li>
        </ul>
      </details>
    </section>

    <div class="dashboard-center">
      <section class="dashboard-card map-card" aria-labelledby="region-title">
        <header class="dashboard-heading">
          <div>
            <h3 id="region-title">지역별기기현황</h3>
            <p>
              {{
                selectedRegion
                  ? selectedRegion + " 선택됨"
                  : "부산 전체 · 지역을 선택하세요"
              }}
            </p>
          </div>
          <button
            class="dashboard-button"
            type="button"
            @click="$emit('region', '')"
          >
            초기화
          </button>
        </header>
        <div class="map-viewport">
          <svg
            viewBox="8 0 370 380"
            preserveAspectRatio="xMidYMid meet"
            class="busan-map"
            role="group"
            aria-label="부산 구·군별 기기 수와 지역 필터"
          >
            <g
              v-for="district in districts"
              :key="district.name"
              class="map-district"
              :class="{ selected: selectedRegion === district.name }"
              role="button"
              tabindex="0"
              :aria-label="`${district.name} ${regionCounts[district.name]}대`"
              :aria-pressed="selectedRegion === district.name"
              @click="$emit('region', district.name)"
              @keydown.enter.prevent="$emit('region', district.name)"
              @keydown.space.prevent="$emit('region', district.name)"
            >
              <title>
                {{ district.name }} {{ regionCounts[district.name] }}대
              </title>
              <path :d="district.path" />
            </g>
            <g class="map-leaders" aria-hidden="true">
              <line
                v-for="district in districts"
                :key="district.name"
                :x1="district.label[0]"
                :y1="district.label[1]"
                :x2="labelPosition(district)[0]"
                :y2="labelPosition(district)[1] + 6"
              />
            </g>
            <g
              v-for="district in districts"
              :key="'label-' + district.name"
              class="map-label"
              :class="{ selected: selectedRegion === district.name }"
              :transform="`translate(${labelPosition(district).join(',')})`"
              aria-hidden="true"
              @click="$emit('region', district.name)"
            >
              <rect
                class="map-label-hit"
                :x="-labelWidth(district) / 2"
                y="-31"
                :width="labelWidth(district)"
                height="53"
              />
              <text y="-9">{{ district.name }}</text>
              <rect
                :x="-countWidth(district) / 2"
                y="-2"
                :width="countWidth(district)"
                height="22"
                rx="11"
              />
              <text class="map-count" y="14">
                {{ regionCounts[district.name] }}
              </text>
            </g>
          </svg>
        </div>
        <p class="dashboard-note">
          {{ selectedRegion ? selectedRegion + " · " : "" }}검색·지역 조건이
          적용된 전체 기기 기준
        </p>
      </section>

      <section
        class="dashboard-card overuse-card"
        aria-labelledby="overuse-title"
      >
        <header class="dashboard-heading">
          <div>
            <h3 id="overuse-title">초과사용목록</h3>
            <p>
              24시간 이상 연속 활성 ·
              <strong class="overuse-count" :class="{ present: overuse.length }"
                >{{ overuse.length }}대</strong
              >
            </p>
          </div>
        </header>
        <div v-if="overuse.length" class="dashboard-scroll">
          <table class="dashboard-table overuse-table">
            <thead>
              <tr>
                <th>학교명</th>
                <th>학교지역</th>
                <th>위치</th>
                <th>상태지속시간</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="device in overuse"
                :key="device.device_uuid"
                class="overuse-row"
                tabindex="0"
                @click="$emit('detail', device)"
                @keydown.enter="$emit('detail', device)"
                @keydown.space.prevent="$emit('detail', device)"
              >
                <td>{{ device.school_name || "-" }}</td>
                <td>{{ device.school_region || "-" }}</td>
                <td>{{ device.app_location || "-" }}</td>
                <td class="overuse-hours">{{ duration(device) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="!overuse.length" class="overuse-empty">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            aria-hidden="true"
          >
            <rect x="3" y="4" width="18" height="13" />
            <path d="M12 17v4M7 21h10" />
          </svg>
          <p>24시간 이상 연속 사용 중인 기기가 없습니다.</p>
        </div>
      </section>
    </div>

    <section
      class="dashboard-card board-list-card"
      aria-labelledby="board-list-title"
    >
      <header class="dashboard-heading">
        <div>
          <h3 id="board-list-title">전자칠판 목록</h3>
          <p class="list-total">총 {{ devices.length }}대 · {{ range }}</p>
        </div>
        <button
          class="dashboard-button"
          type="button"
          :disabled="!devices.length"
          @click="$emit('pdf')"
        >
          PDF 미리보기
        </button>
      </header>
      <div class="dashboard-scroll">
        <table class="dashboard-table board-table">
          <thead>
            <tr>
              <th>
                <button @click="$emit('sort', 'status')">
                  상태 {{ sortIcon("status") }}
                </button>
              </th>
              <th>
                <button @click="$emit('sort', 'schoolName')">
                  학교명 {{ sortIcon("schoolName") }}
                </button>
              </th>
              <th>위치</th>
              <th>
                <button @click="$emit('sort', 'totalUsage')">
                  총사용시간 {{ sortIcon("totalUsage") }}
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="device in pageDevices"
              :key="device.device_uuid"
              class="board-row"
              tabindex="0"
              @click="$emit('detail', device)"
              @keydown.enter="$emit('detail', device)"
              @keydown.space.prevent="$emit('detail', device)"
            >
              <td>
                <span
                  class="board-status"
                  :class="{ active: isActive(device) }"
                  :title="deviceState(device) || '분류 확인 불가'"
                  >{{ isActive(device) ? "활성화" : "비활성화" }}</span
                >
              </td>
              <td class="board-school">{{ device.school_name || "-" }}</td>
              <td>{{ device.app_location || "-" }}</td>
              <td class="board-usage">
                {{ formatUsage(device.total_usage_sec) }}
              </td>
            </tr>
            <tr v-if="!devices.length">
              <td colspan="4" class="dashboard-empty">
                검색 조건에 맞는 기기가 없습니다.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <nav class="dashboard-pagination" aria-label="기기 목록 페이지">
        <button
          type="button"
          :disabled="page === 1"
          @click="$emit('page', page - 1)"
        >
          이전</button
        ><span>{{ page }} / {{ totalPages }}</span
        ><button
          type="button"
          :disabled="page === totalPages"
          @click="$emit('page', page + 1)"
        >
          다음
        </button>
      </nav>
    </section>
  </div>
</template>

<script setup>
/* global defineProps, defineEmits */
import { computed } from "vue";
import { Bar } from "vue-chartjs";
import districts from "@/assets/busanDistricts.json";
import { USAGE_BUCKETS } from "@/utils/usageStatistics";
import { statusHours } from "@/utils/deviceStatus";
import {
  overuseDevices,
  dashboardDistribution,
  dashboardRegionCounts
} from "@/utils/usageDashboard";

const props = defineProps({
  devices: { type: Array, required: true },
  selectedRegion: { type: String, default: "" },
  page: { type: Number, required: true },
  pageDevices: { type: Array, required: true },
  totalPages: { type: Number, required: true },
  range: { type: String, required: true },
  usageSummary: { type: Object, required: true },
  statusSummary: { type: Object, required: true },
  formatUsage: { type: Function, required: true },
  deviceState: { type: Function, required: true },
  sortIcon: { type: Function, required: true }
});
defineEmits(["detail", "statistics", "region", "pdf", "page", "sort"]);
// Fit presentation text to the available width; the underlying metric is unchanged.
const valueObservers = new WeakMap();
function fitMetricValue(element) {
  if (!element.isConnected) return;
  const line = element.querySelector(".metric-value-line");
  if (!line || !element.clientWidth) return;
  element.style.setProperty("--metric-scale", "1");
  const naturalWidth = line.getBoundingClientRect().width;
  element.style.setProperty(
    "--metric-scale",
    String(Math.min(1, (element.clientWidth - 1) / Math.max(1, naturalWidth)))
  );
}
const vFitMetric = {
  mounted(element) {
    let width = -1;
    const observer = new ResizeObserver(() => {
      if (element.clientWidth !== width) {
        width = element.clientWidth;
        fitMetricValue(element);
      }
    });
    valueObservers.set(element, observer);
    observer.observe(element);
    fitMetricValue(element);
    document.fonts?.ready.then(() => fitMetricValue(element));
  },
  updated: fitMetricValue,
  unmounted(element) {
    valueObservers.get(element)?.disconnect();
    valueObservers.delete(element);
  }
};
const buckets = USAGE_BUCKETS;
const isActive = (device) =>
  String(device.status || "").toUpperCase() === "ACTIVE";
const overuse = computed(() => overuseDevices(props.devices));
const distribution = computed(() => dashboardDistribution(props.devices));
const regionCounts = computed(() => dashboardRegionCounts(props.devices));
const duration = (device) => `${Math.floor(statusHours(device))}시간`;
const metrics = computed(() => {
  const count = props.usageSummary.count;
  const active = props.statusSummary.active;
  return [
    {
      label: "전체 설치 기기",
      value: `${count.toLocaleString()}대`,
      icon: "▣",
      tone: "metric-total"
    },
    {
      label: "활성화 기기",
      value: `${active.toLocaleString()}대`,
      icon: "⏻",
      tone: "metric-active"
    },
    {
      label: "비활성화 기기",
      value: `${(count - active).toLocaleString()}대`,
      icon: "!",
      tone: "metric-inactive"
    },
    {
      label: "평균 사용시간",
      value: props.usageSummary.averageText,
      icon: "◷",
      tone: "metric-average"
    }
  ];
});
const chartData = computed(() => ({
  labels: ["0–2만", "2–3만", "3–4만", "4–5만", "5만 이상"],
  datasets: [
    {
      label: "기기 수",
      data: distribution.value.counts,
      backgroundColor: buckets.map((bucket) => bucket.color),
      maxBarThickness: 34,
      barPercentage: 0.75,
      categoryPercentage: 0.85
    }
  ]
}));
const distributionLabel = computed(() =>
  buckets
    .map(
      (bucket, index) =>
        `${bucket.label}: ${distribution.value.counts[index]}대`
    )
    .join(", ")
);
const chartOptions = {
  animation: false,
  responsive: true,
  maintainAspectRatio: false,
  layout: { padding: { top: 10, right: 4, bottom: 0, left: 0 } },
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: { title: (items) => buckets[items[0].dataIndex].label }
    }
  },
  scales: {
    x: {
      grid: { display: false },
      border: { display: false },
      title: {
        display: true,
        text: "누적 사용시간 (시간)",
        color: "#647184",
        font: { size: 12 }
      },
      ticks: {
        color: "#647184",
        font: { size: 11 },
        maxRotation: 0,
        autoSkip: false
      }
    },
    y: {
      beginAtZero: true,
      border: { display: false },
      grid: { color: "#edf1f6", drawTicks: false },
      title: {
        display: true,
        text: "기기 수",
        color: "#647184",
        font: { size: 12 }
      },
      ticks: { precision: 0, color: "#647184", font: { size: 11 }, padding: 8 },
      suggestedMax: 1
    }
  }
};
// Separate labels in the small, adjacent downtown districts without changing boundaries.
const labelOverrides = {
  중구: [210, 295],
  서구: [145, 295],
  동구: [200, 240],
  영도구: [245, 350],
  부산진구: [181, 185],
  동래구: [246, 130],
  남구: [275, 295],
  북구: [164, 125],
  해운대구: [322, 185],
  사하구: [120, 350],
  금정구: [229, 75],
  강서구: [60, 240],
  연제구: [245, 185],
  수영구: [290, 240],
  사상구: [115, 195],
  기장군: [315, 65]
};
function labelWidth(district) {
  return Math.max(countWidth(district), district.name.length * 14) + 8;
}
function countWidth(district) {
  return Math.max(
    34,
    String(regionCounts.value[district.name]).length * 8 + 12
  );
}
function labelPosition(district) {
  return labelOverrides[district.name] || district.label;
}
</script>

<style scoped>
.usage-dashboard {
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  grid-template-rows: 300px 390px;
  gap: 16px;
  min-width: 0;
}
.dashboard-card {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  background: #fff;
  border: 1px solid #e3e8ef;
  padding: 20px;
  box-shadow: 0 2px 6px rgb(21 37 62 / 3%);
}
.dashboard-center {
  grid-column: 5 / 9;
  grid-row: 1 / 3;
  display: grid;
  grid-template-rows: minmax(0, 60fr) minmax(0, 40fr);
  gap: 16px;
  min-width: 0;
  min-height: 0;
}
.distribution-card {
  grid-column: 1 / 5;
  grid-row: 2;
}
.dashboard-metrics {
  grid-column: 1 / 5;
  grid-row: 1;
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  grid-template-rows: repeat(2, minmax(0, 1fr));
  gap: 12px;
  min-width: 0;
}
.dashboard-metric {
  grid-column: span 6;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
  padding: 20px;
  background: #fff;
  border: 1px solid #e3e8ef;
  box-shadow: 0 2px 6px rgb(21 37 62 / 3%);
  min-width: 0;
  overflow: hidden;
}
.metric-main {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  min-width: 0;
}
.metric-copy {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: right;
}
.dashboard-metric h3 {
  margin: 0;
  text-align: right;
  font-size: 13px;
  color: #637084;
  font-weight: 500;
  line-height: 1.4;
}
.metric-value {
  --metric-base: 32px;
  display: block;
  width: 100%;
  min-width: 0;
  font-size: calc(var(--metric-base) * var(--metric-scale, 1));
  line-height: 1.2;
  font-weight: 800;
  color: #111;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.04em;
}
.metric-value-line {
  display: inline-block;
  white-space: pre;
}
.metric-unit {
  margin-left: 2px;
  vertical-align: baseline;
  font-size: 0.5625em;
  font-weight: 600;
  letter-spacing: -0.02em;
}
.metric-average .metric-value {
  --metric-base: 24px;
}
.metric-average .metric-unit {
  font-size: 0.55em;
}
.metric-symbol {
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  font-size: 25px;
  color: #0066ff;
  background: transparent;
}
.metric-inactive .metric-symbol {
  background: transparent;
  color: #737980;
}
.metric-average .metric-symbol {
  font-size: 24px;
}
.board-list-card {
  grid-column: 9 / 13;
  grid-row: 1 / 3;
}
.dashboard-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  gap: 8px;
  margin-bottom: 12px;
  min-height: 34px;
}
.dashboard-heading h3 {
  margin: 0;
  color: #171717;
  font-size: 18px;
  font-weight: 750;
  overflow-wrap: anywhere;
}
.dashboard-heading p {
  margin: 5px 0 0;
  color: #647184;
  font-size: 12px;
  line-height: 1.4;
}
.dashboard-button {
  border: 1px solid #dce3ec;
  background: #fff;
  color: #465267;
  min-height: 34px;
  padding: 7px 10px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
}
.dashboard-button:hover {
  background: #f5f5f3;
}
.dashboard-button:disabled {
  opacity: 0.45;
  cursor: default;
}
.dashboard-scroll {
  min-height: 0;
  min-width: 0;
  flex: 1;
  overflow: auto;
  border: 0;
  border-top: 1px solid #edf1f6;
}
.dashboard-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
}
.overuse-table {
  min-width: 0;
}
.board-table {
  min-width: 0;
}
.dashboard-table th {
  position: sticky;
  top: 0;
  z-index: 1;
}
.dashboard-table th button {
  border: 0;
  background: transparent;
  padding: 0;
  color: inherit;
  font: inherit;
  cursor: pointer;
}
.dashboard-table tbody tr {
  cursor: pointer;
}
.dashboard-table tbody tr:hover {
  background: #f6f9fe;
}
.dashboard-table tbody tr:focus-visible {
  outline: 2px solid #171717;
  outline-offset: -2px;
}
.dashboard-pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 14px;
  flex-shrink: 0;
  padding-top: 14px;
  font-size: 13px;
  color: #647184;
  font-variant-numeric: tabular-nums;
}
.dashboard-pagination button {
  background: #fff;
  border: 1px solid #dededb;
  padding: 6px 10px;
  cursor: pointer;
}
.dashboard-pagination button:disabled {
  opacity: 0.35;
  cursor: default;
}
.board-status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #707b8b;
  font-size: 13px;
  white-space: nowrap;
}
.board-status::before {
  content: "";
  width: 6px;
  height: 6px;
  background: currentColor;
  border-radius: 50%;
  flex-shrink: 0;
}
.board-status.active {
  color: #2463eb;
}
.dashboard-empty {
  color: #888;
  text-align: center;
  font-size: 12px;
  padding: 22px 10px;
  cursor: default;
}
.dashboard-chart {
  min-height: 0;
  flex: 1;
  position: relative;
}
.chart-empty {
  flex: 1;
  display: grid;
  place-items: center;
}
.dashboard-note {
  color: #647184;
  font-size: 12px;
  margin: 7px 0 0;
  line-height: 1.4;
}
.chart-values {
  flex-shrink: 0;
  font-size: 12px;
  color: #647184;
  max-height: 70px;
  overflow: auto;
  margin-top: 6px;
}
.chart-values summary {
  cursor: pointer;
}
.chart-values ul {
  margin: 5px 0;
  padding-left: 18px;
}
.map-viewport {
  min-height: 0;
  flex: 1;
  display: grid;
  place-items: center;
}
.busan-map {
  width: 100%;
  height: 100%;
}
.map-district {
  cursor: pointer;
}
.map-district path {
  fill: #eaf0f6;
  stroke: #fff;
  stroke-width: 1.2;
  transition: fill 0.15s;
}
.map-district:hover path,
.map-district:focus-visible path {
  fill: #d6e5f7;
  stroke: #0066ff;
  stroke-width: 1.4;
}
.map-district.selected path {
  fill: #2563eb;
  stroke: #fff;
  stroke-width: 1.5;
}
.map-leaders {
  pointer-events: none;
  stroke: #91acc9;
  stroke-width: 0.7;
}
.map-label {
  cursor: pointer;
}
.map-label text {
  font-size: 15px;
  text-anchor: middle;
  fill: #354153;
  font-weight: 600;
}
.map-label rect {
  fill: #0066ff;
}
.map-label.selected rect {
  fill: #1d4ed8;
}
.map-label.selected > text:first-of-type {
  fill: #1748b0;
  stroke: #fff;
  stroke-width: 3px;
  paint-order: stroke;
}
.map-label .map-label-hit,
.map-label.selected .map-label-hit {
  fill: transparent;
  pointer-events: all;
}
.map-label .map-count {
  fill: #fff;
  font-size: 14px;
  font-variant-numeric: tabular-nums;
}
.metric-symbol svg {
  width: 30px;
  height: 30px;
}

.map-card {
  padding: 16px 20px;
}
.map-card .dashboard-heading {
  margin-bottom: 10px;
}
.overuse-count {
  font-weight: 600;
  color: inherit;
  font-variant-numeric: tabular-nums;
}
.overuse-count.present {
  color: #b76123;
}
.overuse-empty {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #647184;
  text-align: center;
}
.overuse-empty svg {
  width: 36px;
  height: 36px;
  padding: 7px;
  background: #f3f5f8;
  color: #99a5b6;
  box-sizing: content-box;
}
.overuse-empty p {
  margin: 0;
  max-width: 250px;
  font-size: 13px;
  line-height: 1.7;
  word-break: keep-all;
}
.dashboard-button:focus-visible,
.dashboard-pagination button:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: 2px;
}
.dashboard-pagination button {
  min-height: 32px;
  border-color: #dce3ec;
  color: #465267;
  font-size: 12px;
}
.dashboard-pagination button:hover:not(:disabled) {
  background: #f2f6fc;
}
.board-table th:nth-child(1) {
  width: 23%;
}
.board-table th:nth-child(2) {
  width: 27%;
}
.board-table th:nth-child(3) {
  width: 20%;
}
.board-table th:nth-child(4) {
  width: 30%;
}
@media (max-width: 1100px) {
  .usage-dashboard {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    grid-template-rows: 300px 390px 520px;
  }
  .dashboard-center {
    grid-column: 2;
    grid-template-rows: minmax(0, 1fr) 275px;
  }
  .dashboard-metrics {
    grid-column: 1;
  }
  .distribution-card {
    grid-column: 1;
  }
  .board-list-card {
    grid-column: 1 / 3;
    grid-row: 3;
  }
}
@media (max-width: 640px) {
  .usage-dashboard {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: 240px 340px auto 520px;
    gap: 12px;
  }
  .dashboard-center {
    grid-column: 1;
    grid-row: 3;
    grid-template-rows: 420px 280px;
    gap: 12px;
  }
  .dashboard-metrics {
    grid-column: 1;
    grid-row: 1;
  }
  .distribution-card {
    grid-column: 1;
    grid-row: 2;
  }
  .board-list-card {
    grid-column: 1;
    grid-row: 4;
  }
  .dashboard-card {
    padding: 16px;
  }
  .dashboard-metric {
    padding: 20px;
    gap: 8px;
  }
}
</style>

<style>
/* Shared admin table rules are !important; scope compact overrides to this dashboard. */
.admin-layout .usage-dashboard .dashboard-table th {
  padding: 11px 10px !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  color: #647184 !important;
  background: #f7f9fc !important;
  border: 0 !important;
  border-bottom: 1px solid #e9eef5 !important;
  letter-spacing: 0;
}
.admin-layout .usage-dashboard .dashboard-table td {
  padding: 13px 10px !important;
  height: 50px;
  font-size: 13px !important;
  color: #364255 !important;
  line-height: 1.55 !important;
  border: 0 !important;
  border-bottom: 1px solid #edf1f6 !important;
  white-space: normal;
  overflow-wrap: anywhere;
  font-variant-numeric: tabular-nums;
}
.admin-layout .usage-dashboard .board-table th,
.admin-layout .usage-dashboard .board-table td {
  padding-left: 6px !important;
  padding-right: 6px !important;
}
.admin-layout .usage-dashboard .dashboard-table .board-school {
  font-weight: 600;
  color: #223047 !important;
  text-align: left;
}
.admin-layout .usage-dashboard .board-table th:last-child,
.admin-layout .usage-dashboard .dashboard-table .board-usage {
  text-align: right;
  white-space: normal;
}
.admin-layout .usage-dashboard .dashboard-table .overuse-hours {
  color: #b76123 !important;
  font-weight: 600;
  white-space: nowrap;
}
.admin-layout .usage-dashboard .overuse-table td {
  padding: 10px !important;
  height: 44px;
}
.admin-layout .usage-dashboard .board-status::before {
  border-radius: 50% !important;
}
.admin-layout .usage-dashboard .overuse-table th:nth-child(1) {
  width: 28%;
}
.admin-layout .usage-dashboard .overuse-table th:nth-child(2) {
  width: 24%;
}
.admin-layout .usage-dashboard .overuse-table th:nth-child(3) {
  width: 18%;
}
.admin-layout .usage-dashboard .overuse-table th:nth-child(4) {
  width: 30%;
  white-space: normal;
}
</style>
